import { useState } from 'react';
import { toast } from 'sonner';
import { ArrowRightLeft, GraduationCap, RotateCcw } from 'lucide-react';
import { TransferTrainingModal } from './TransferTrainingModal';
import {
  usePersonTrainingModules,
  useAssignTrainingModule,
  useReopenTrainingModule,
  useSetTrainingAssignAllExcluded,
} from '../../hooks/onboardingOps';
import { CollapsibleSection } from '../ui/CollapsibleSection';
import { Button } from '../ui/Button';
import { Pill } from '../ui/Pill';
import { ApiError } from '../../lib/api';
import { ItemStatusPill, canReopen } from './itemStatus';

/**
 * Per-person training module assignment. Modules are normally driven by the
 * role template; this lets HR assign a specific module to one person and
 * reopen a completed one. Reopening voids the person's prior attempts
 * server-side, so a retake is required. Unpublished modules are shown but
 * can't be assigned (the inductee would have nothing to take).
 */
export function PersonTrainingPanel({
  personId,
  excludedFromEveryone = false,
}: {
  personId: string;
  /** Person opted out of the "everyone" allocation (metadata flag). */
  excludedFromEveryone?: boolean;
}) {
  const modules = usePersonTrainingModules(personId);
  const set = useAssignTrainingModule(personId);
  const reopen = useReopenTrainingModule(personId);
  const setExcluded = useSetTrainingAssignAllExcluded(personId);
  const [excluded, setExcludedState] = useState(excludedFromEveryone);
  const [transferOpen, setTransferOpen] = useState(false);

  async function toggleExcluded(next: boolean) {
    setExcludedState(next); // optimistic
    try {
      const r = await setExcluded.mutateAsync(next);
      if (next && r.removed > 0) {
        toast.success(
          `Excluded — removed ${r.removed} outstanding module${r.removed === 1 ? '' : 's'}`,
        );
      }
    } catch (err) {
      setExcludedState(!next);
      toast.error(err instanceof ApiError ? err.message : 'Could not update');
    }
  }

  if (modules.isLoading || !modules.data) return null;
  if (modules.data.length === 0) return null;

  const assignedCount = modules.data.filter((m) => m.assigned).length;

  return (
    <CollapsibleSection
      icon={<GraduationCap className="h-4 w-4 text-fg-muted" />}
      title="Training modules"
      trailing={`${assignedCount} assigned`}
    >
      <p className="mb-2 text-xs text-fg-muted">
        Tick the e-learning modules this person needs to complete. Role templates pre-select some;
        adjust per person here.
      </p>
      <label className="mb-3 flex items-start gap-2.5 rounded-token-sm border border-line/10 bg-surface-2 p-2.5 text-sm">
        <input
          type="checkbox"
          className="mt-0.5"
          checked={excluded}
          disabled={setExcluded.isPending}
          onChange={(e) => void toggleExcluded(e.target.checked)}
        />
        <span>
          <span className="font-medium text-fg">Exclude from &ldquo;Everyone&rdquo; training</span>
          <span className="mt-0.5 block text-xs text-fg-muted">
            Auto-assigned (&ldquo;Everyone&rdquo;) modules are never added to this person, and any
            still-outstanding ones are removed now. Use for generic / back-office accounts that
            don&rsquo;t need training.
          </span>
        </span>
      </label>
      <ul className="divide-y divide-divider/10">
        {modules.data.map((m) => (
          <li key={m.id} className="flex items-center justify-between gap-2 py-2.5 text-sm">
            <span className="flex min-w-0 items-center gap-2 text-fg">
              <span className="truncate">{m.title}</span>
              <ItemStatusPill status={m.status} />
              {!m.published ? (
                <Pill tone="warning" size="sm">
                  No published version
                </Pill>
              ) : null}
            </span>
            <div className="flex shrink-0 items-center gap-3">
              {m.assigned && canReopen(m.status) ? (
                <Button
                  variant="ghost"
                  size="sm"
                  iconLeft={<RotateCcw className="h-3.5 w-3.5" />}
                  loading={reopen.isPending}
                  onClick={async () => {
                    try {
                      await reopen.mutateAsync(m.id);
                      toast.success('Reopened for retake');
                    } catch (err) {
                      toast.error(err instanceof ApiError ? err.message : 'Could not reopen');
                    }
                  }}
                >
                  Reopen for retake
                </Button>
              ) : null}
              <label className="flex items-center gap-2 text-fg-muted">
                <input
                  type="checkbox"
                  checked={m.assigned}
                  disabled={set.isPending || (!m.assigned && !m.published)}
                  onChange={async (e) => {
                    try {
                      await set.mutateAsync({ moduleId: m.id, assigned: e.target.checked });
                    } catch (err) {
                      toast.error(err instanceof ApiError ? err.message : 'Could not update');
                    }
                  }}
                />
                {m.assigned ? 'Assigned' : 'Not assigned'}
              </label>
            </div>
          </li>
        ))}
      </ul>
      <div className="mt-3 border-t border-divider/10 pt-3">
        <Button
          variant="ghost"
          size="sm"
          iconLeft={<ArrowRightLeft className="h-3.5 w-3.5" />}
          onClick={() => setTransferOpen(true)}
        >
          Transfer passes from a duplicate record
        </Button>
        <p className="mt-1 text-xs text-fg-dim">
          For passes that landed on another record for the same person (e.g. one auto-created by
          their first SSO login).
        </p>
      </div>
      <TransferTrainingModal
        personId={personId}
        open={transferOpen}
        onClose={() => setTransferOpen(false)}
      />
    </CollapsibleSection>
  );
}
